import { useCallback, useState } from 'react';
import ConnectionsService from '../services/ConnectionsService';
import { errorText } from '../utils/format';
import useAsync from './useAsync';

export function useConnectionsViewModel() {
  const load = useCallback(() => ConnectionsService.list(), []);
  const { data, loading, error, reload } = useAsync(load);
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState(null);

  const connections = data || [];
  const ai = connections.find((c) => c.type === 'ai_provider') || null;
  const orka = connections.find((c) => c.type === 'estore_mcp') || connections.find((c) => c.type === 'orka') || null;
  const analytics = connections.find((c) => c.type === 'analytics_mcp') || null;
  const whatsapp = connections.find((c) => c.type === 'whatsapp_mcp') || null;
  const [storeChoices, setStoreChoices] = useState(null);

  const act = async (key, fn, successText) => {
    setBusy(key);
    setFeedback(null);
    try {
      await fn();
      await reload({ silent: true });
      setFeedback({ severity: 'success', text: successText });
      return true;
    } catch (err) {
      setFeedback({ severity: 'error', text: errorText(err) });
      return false;
    } finally {
      setBusy('');
    }
  };

  return {
    ai,
    orka,
    loading,
    error,
    busy,
    feedback,
    clearFeedback: () => setFeedback(null),
    reload,
    saveAiKey: (provider, apiKey) => act('ai', () => ConnectionsService.saveAiKey(provider, apiKey), 'AI key saved.'),
    analytics,
    whatsapp,
    saveWhatsappKey: (apiKey) => act('whatsapp', () => ConnectionsService.saveWhatsappKey(apiKey), 'WhatsApp connected.'),
    saveAnalyticsKey: (apiKey) => act('analytics', () => ConnectionsService.saveAnalyticsKey(apiKey), 'Analytics connected.'),
    storeChoices,
    saveStoreKey: async (apiKey, storeId) => {
      setBusy('orka');
      setFeedback(null);
      try {
        await ConnectionsService.saveStoreKey(apiKey, storeId);
        setStoreChoices(null);
        await reload({ silent: true });
        setFeedback({ severity: 'success', text: 'Store connected.' });
        return true;
      } catch (err) {
        if (err.code === 'store_required' && err.details?.stores) setStoreChoices(err.details.stores);
        else setStoreChoices(null);
        setFeedback({ severity: err.code === 'store_required' ? 'info' : 'error', text: errorText(err) });
        return false;
      } finally {
        setBusy('');
      }
    },
    remove: (id) => act(`remove-${id}`, () => ConnectionsService.remove(id), 'Connection removed.'),
  };
}
